import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  ListInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TimePicker,
  useToast,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  ReportBackendTypes,
  ReportTemplate,
  useGetReport,
  useGetReportExportFormats,
  useScheduleReport,
} from '@ais-ui/websockets';
import { Weekday, weekdayToNumber } from '../weekdays';
import { Month, monthToNumber } from '../months';
import { useLocalization } from '@ais-ui/locale-management';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useGetScheduleHint } from '../useGetScheduleHint';
import { getMailActionOptions } from './utils/mailActionMapping';

const scheduleReportFormSchema = z.object({
  interval: z.string().nonempty(),
  intervalStart: z.string(),
  endDate: z.date(),
  maxKept: z.number(),
  executionTime: z.string(),
  email: z.array(z.string()).optional(),
  mailAction: z.string().optional(),
});

type ScheduleFormFormValues = z.infer<typeof scheduleReportFormSchema>;

type NumberInputEvent = React.ChangeEvent<HTMLInputElement>;

interface ScheduleReportsProps {
  reportTemplate: ReportTemplate;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ScheduleHintTextProps {
  data: ScheduleFormFormValues;
}

const ScheduleHintText: React.FC<ScheduleHintTextProps> = ({ data }) => {
  const getScheduleHint = useGetScheduleHint();
  const [hintSchedule, setHintSchedule] = useState<string | null>(null);
  const [hintNextJob, setHintNextJob] = useState<string | null>(null);

  useEffect(() => {
    const hint = getScheduleHint(
      data.interval,
      data.intervalStart,
      data.endDate,
      data.executionTime
    );
    if (!hint) {
      return;
    }
    setHintSchedule(hint.scheduleText);
    setHintNextJob(hint.nextJobText);
  }, [data, getScheduleHint]);

  return (
    <div className="prose text-sm">
      <p>{hintSchedule}</p>
      <p>{hintNextJob}</p>
    </div>
  );
};

export const ScheduleReports = ({
  reportTemplate,
  showDialog,
  setShowDialog,
}: ScheduleReportsProps) => {
  const { messages } = useLocalization();

  // Fetch available export formats for this report type
  const { data: exportFormats, isLoading: isLoadingFormats } = useGetReportExportFormats(reportTemplate.type);

  // Get mail action options based on available export formats
  const mailActionOptions = useMemo(
    () => getMailActionOptions(exportFormats),
    [exportFormats]
  );

  const { timeZone } = useTimeZoneContext();
  const { data: report, isLoading } = useGetReport(
    reportTemplate.tplUuid,
    timeZone
  );

  const form = useForm<ScheduleFormFormValues>({
    resolver: zodResolver(scheduleReportFormSchema),
    defaultValues: {
      interval: 'weekly',
      intervalStart: Weekday.Monday,
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      maxKept: 10,
      executionTime: '00:00',
      email: [],
      mailAction: '',
    },
  });

  // Update form with existing values once report is loaded
  useEffect(() => {
    if (report?.params?.MC) {
      form.setValue('email', report.params.MC.who || []);
      form.setValue('mailAction', report.params.MC.action || '');
    }
  }, [report, form]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const interval = useMemo(() => form.watch('interval'), [form.watch]);

  useEffect(() => {
    switch (interval) {
      case 'weekly':
        form.setValue('intervalStart', Weekday.Monday);
        break;
      case 'yearly':
        form.setValue('intervalStart', Month.January);
        break;
      default:
        form.setValue('intervalStart', '');
    }
  }, [interval]);

  const { mutate } = useScheduleReport();

  const handleNumberChange = (
    e: NumberInputEvent,
    onChange: (value: number) => void
  ): void => {
    if (!isNaN(e.target.valueAsNumber)) {
      onChange(e.target.valueAsNumber);
    }
    form.clearErrors();
  };

  const { toast } = useToast();

  const disablePastDates = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to the start of today
    return date <= today; // Disable today and dates before today
  };

  const onScheduleReport = (data: ScheduleFormFormValues) => {
    if (!report) {
      return;
    }

    setIsSubmitting(true);

    const name = `${reportTemplate.tplName}-schedule`;
    const templateId = reportTemplate.tplUuid;

    const reportParams = report.params;

    if (!reportParams.RS) {
      reportParams.RS = {};
    }

    // Update MC (mail config) with email and action
    if (!reportParams.MC) {
      reportParams.MC = { who: [], action: '' };
    }
    const emails = data.email || [];
    reportParams.MC.who = emails;
    reportParams.MC.action = emails.length > 0 ? (data.mailAction || 'notify') : '';

    let intervalStart = -1;
    if (data.interval === 'weekly') {
      intervalStart = weekdayToNumber(data.intervalStart as Weekday);
    }
    if (data.interval === 'yearly') {
      intervalStart = monthToNumber(data.intervalStart as Month);
    }

    reportParams.RS.SI = data.interval;
    reportParams.RS.SIS = intervalStart;
    reportParams.RS.EDAUSR = data.endDate.toISOString();
    reportParams.RS.MAXKEPT = data.maxKept;
    reportParams.RS.EXECTIME = data.executionTime;

    mutate(
      {
        type: reportTemplate.type as ReportBackendTypes,
        reportParams: JSON.stringify(reportParams),
        name: name,
        templateId: templateId,
        interval: data.interval,
        intervalStart: intervalStart,
        endTime: data.endDate.toISOString(),
      },
      {
        onSuccess: () => {
          toast({
            className: 'schedule-report-success-toast',
            title: messages.genericToastTitleSuccess,
            description: messages.reportsCreateScheduleSuccessMessage,
          });
          setIsSubmitting(false);
          setShowDialog(false);
        },
        onError: () => {
          toast({
            className: 'schedule-report-error-toast',
            title: messages.genericToastTitleError,
            description: messages.reportsCreateScheduleFailedMessage,
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        },
      }
    );
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="add-update-service-dialog">
        <DialogHeader>
          <DialogTitle>{messages.reportsCreateScheduleTitle}</DialogTitle>
          <DialogDescription>
            {messages.reportsCreateScheduleSubtitle}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onScheduleReport)}
            className="space-y-8"
          >
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="interval"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interval</FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="weekly" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="daily" value="daily">
                            {messages.reportsCreateScheduleSelectDaily}
                          </SelectItem>
                          <SelectItem key="weekly" value="weekly">
                            {messages.reportsCreateScheduleSelectWeekly}
                          </SelectItem>
                          <SelectItem key="monthly" value="monthly">
                            {messages.reportsCreateScheduleSelectMonthly}
                          </SelectItem>
                          <SelectItem key="quarterly" value="quarterly">
                            {messages.reportsCreateScheduleSelectQuarterly}
                          </SelectItem>
                          <SelectItem key="yearly" value="yearly">
                            {messages.reportsCreateScheduleSelectYearly}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch('interval') === 'weekly' && (
                <FormField
                  control={form.control}
                  name="intervalStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {messages.reportsCreateScheduleWeekdayLabel}
                      </FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={Weekday.Monday}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue>{field.value}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem
                              key={Weekday.Monday}
                              value={Weekday.Monday}
                            >
                              {Weekday.Monday}
                            </SelectItem>
                            <SelectItem
                              key={Weekday.Tuesday}
                              value={Weekday.Tuesday}
                            >
                              {Weekday.Tuesday}
                            </SelectItem>
                            <SelectItem
                              key={Weekday.Wednesday}
                              value={Weekday.Wednesday}
                            >
                              {Weekday.Wednesday}
                            </SelectItem>
                            <SelectItem
                              key={Weekday.Thursday}
                              value={Weekday.Thursday}
                            >
                              {Weekday.Thursday}
                            </SelectItem>
                            <SelectItem
                              key={Weekday.Friday}
                              value={Weekday.Friday}
                            >
                              {Weekday.Friday}
                            </SelectItem>
                            <SelectItem
                              key={Weekday.Saturday}
                              value={Weekday.Saturday}
                            >
                              {Weekday.Saturday}
                            </SelectItem>
                            <SelectItem
                              key={Weekday.Sunday}
                              value={Weekday.Sunday}
                            >
                              {Weekday.Sunday}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
              {form.watch('interval') === 'yearly' && (
                <FormField
                  control={form.control}
                  name="intervalStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {messages.reportsCreateScheduleMonthLabel}
                      </FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={Month.January}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue>{field.value}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem
                              key={Month.January}
                              value={Month.January}
                            >
                              {Month.January}
                            </SelectItem>

                            <SelectItem
                              key={Month.February}
                              value={Month.February}
                            >
                              {Month.February}
                            </SelectItem>
                            <SelectItem key={Month.March} value={Month.March}>
                              {Month.March}
                            </SelectItem>
                            <SelectItem key={Month.April} value={Month.April}>
                              {Month.April}
                            </SelectItem>
                            <SelectItem key={Month.May} value={Month.May}>
                              {Month.May}
                            </SelectItem>
                            <SelectItem key={Month.June} value={Month.June}>
                              {Month.June}
                            </SelectItem>
                            <SelectItem key={Month.July} value={Month.July}>
                              {Month.July}
                            </SelectItem>
                            <SelectItem key={Month.August} value={Month.August}>
                              {Month.August}
                            </SelectItem>
                            <SelectItem
                              key={Month.September}
                              value={Month.September}
                            >
                              {Month.September}
                            </SelectItem>
                            <SelectItem
                              key={Month.October}
                              value={Month.October}
                            >
                              {Month.October}
                            </SelectItem>
                            <SelectItem
                              key={Month.November}
                              value={Month.November}
                            >
                              {Month.November}
                            </SelectItem>
                            <SelectItem
                              key={Month.December}
                              value={Month.December}
                            >
                              {Month.December}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="executionTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {messages.reportsCreateScheduleExecutionTimeLabel}
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimePicker
                        value={field.value}
                        onChange={field.onChange}
                      ></TimePicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {messages.reportsCreateScheduleEndScheduleDateLabel}
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        disabledCalendar={disablePastDates}
                      ></DatePicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxKept"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {messages.reportsCreateScheduleMaxKeptLabel}
                    </FormLabel>
                    <FormControl
                      onChange={(e: NumberInputEvent) =>
                        handleNumberChange(e, field.onChange)
                      }
                    >
                      <Input
                        type="number"
                        placeholder={
                          messages.reportsCreateScheduleMaxKeptPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="px-px">
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportMiscellaneousNotifyEmailLabel ||
                          'Email'}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportMiscellaneousNotifyEmailTooltip ||
                          'Email addresses to notify when report completes'}
                      </InfoIconTooltip>
                    </div>
                    <FormControl>
                      <ListInput
                        value={field.value || []}
                        onChange={field.onChange}
                        placeholder={
                          messages.reportsCreateReportMiscellaneousNotifyEmailPlaceholder ||
                          'Enter email and press Enter'
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailAction"
                render={({ field }) => (
                  <FormItem className="px-px">
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportMiscellaneousMailActionLabel ||
                          'Email content'}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportMiscellaneousMailActionTooltip ||
                          'Choose what to include in the email when the report completes'}
                      </InfoIconTooltip>
                    </div>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoadingFormats}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingFormats 
                                ? 'Loading...'
                                : (messages.reportsCreateReportMiscellaneousMailActionPlaceholder ||
                                  'Select email content')
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent key={`mail-options-${mailActionOptions.length}`}>
                        {mailActionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <ScheduleHintText data={form.watch()} />
            <DialogFooter className="p-4">
              <Button
                id="schedule-report-confirm-update-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? messages.genericSubmitting
                  : messages.genericSubmit}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
